import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useMaintenance } from "../../context/MaintenanceContext";
import MaintenanceIllustration from "./MaintenanceIllustration";

export default function MaintenancePage() {
  const {
    maintenanceInfo,
    checkMaintenanceStatus,
    isSuperAdmin,
    previewMode,
    setPreviewMode,
  } = useMaintenance();
  const navigate = useNavigate();

  const [isChecking, setIsChecking] = useState(false);
  const [checkFeedback, setCheckFeedback] = useState(null);

  // Compute estimated time in hours (e.g. ~1 hr, ~2 hrs, ~1-2 hrs)
  const estimatedTimeInHours = useMemo(() => {
    // 1. Direct estimatedHours from maintenance record
    if (maintenanceInfo?.estimatedHours) {
      const hours = Number(maintenanceInfo.estimatedHours);
      if (!isNaN(hours) && hours > 0) {
        if (hours < 1) {
          return `${Math.round(hours * 60)} mins`;
        }
        if (Math.abs(hours - Math.round(hours)) < 0.05) {
          const rounded = Math.round(hours);
          return `~${rounded} hr${rounded > 1 ? "s" : ""}`;
        }
        return `~${hours} hrs`;
      }
    }

    return "~2 hrs";
  }, [maintenanceInfo?.estimatedHours]);

  // Handle manual status check
  const handleManualCheck = useCallback(async () => {
    setIsChecking(true);
    setCheckFeedback(null);
    try {
      const res = await checkMaintenanceStatus();
      await new Promise((resolve) => setTimeout(resolve, 600));

      if (res && res.isUnderMaintenance === false) {
        window.location.reload();
        return;
      }

      setCheckFeedback({
        type: "info",
        message:
          "GradeMe AI is still undergoing scheduled maintenance. We'll be back online soon!",
      });
    } catch (err) {
      setCheckFeedback({
        type: "info",
        message:
          "GradeMe AI is still undergoing scheduled maintenance. Thank you for your patience!",
      });
    } finally {
      setIsChecking(false);
      setTimeout(() => setCheckFeedback(null), 5000);
    }
  }, [checkMaintenanceStatus]);

  const customReason = maintenanceInfo?.reason;

  return (
    <div className="custom-maint-wrapper">
      {/* Super Admin Live Preview Alert Banner */}
      {previewMode && (
        <div className="custom-maint-preview-alert">
          <div className="custom-maint-preview-content">
            <i className="fa-solid fa-eye"></i>
            <span>
              <strong>Super Admin Live Preview:</strong> You are viewing the maintenance screen in preview mode. Real users only see this when maintenance is active.
            </span>
          </div>
          <button
            type="button"
            className="custom-maint-preview-exit-btn"
            onClick={() => setPreviewMode(false)}
          >
            Exit Preview
          </button>
        </div>
      )}

      {/* Ambient Pastel Studio Glow Blobs */}
      <div className="custom-maint-ambient-canvas" aria-hidden="true">
        <div className="maint-ambient-blob blob-yellow"></div>
        <div className="maint-ambient-blob blob-pink"></div>
        <div className="maint-ambient-blob blob-green"></div>
        <div className="maint-ambient-blob blob-peach"></div>

        {/* Foreground Depth-of-Field Accents (peering into classroom) */}
        <div className="maint-foreground-desk-left"></div>
        <div className="maint-foreground-desk-right"></div>
      </div>

      {/* Hand-Drawn Background Doodles framing the scene */}
      <div className="custom-maint-doodles" aria-hidden="true">
        {/* Paper Airplane (Top-Left) */}
        <div className="doodle-item doodle-airplane">
          <svg viewBox="0 0 120 90" fill="none" className="doodle-svg">
            <path
              d="M10,80 Q40,75 55,50 Q70,25 35,20 Q10,18 20,45 Q30,65 75,55 Q95,50 110,40"
              stroke="#f43f5e"
              strokeWidth="1.8"
              strokeDasharray="4 4"
            />
            <g transform="translate(75, 20) rotate(18)">
              <polygon points="0,20 36,0 12,28" fill="#ffffff" stroke="#e11d48" strokeWidth="2" />
              <polygon points="12,28 36,0 20,32" fill="#ffe4e6" stroke="#e11d48" strokeWidth="2" />
              <line x1="20" y1="32" x2="36" y2="0" stroke="#e11d48" strokeWidth="2" />
            </g>
          </svg>
        </div>

        {/* Glowing Idea Lightbulb (Top-Left Below Airplane) */}
        <div className="doodle-item doodle-lightbulb">
          <svg viewBox="0 0 80 90" fill="none" className="doodle-svg">
            <line x1="12" y1="20" x2="22" y2="28" stroke="#15803d" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="40" y1="8" x2="40" y2="20" stroke="#15803d" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="68" y1="20" x2="58" y2="28" stroke="#15803d" strokeWidth="2.2" strokeLinecap="round" />
            <path
              d="M25,52 C15,44 15,28 27,20 C37,14 47,16 53,22 C61,30 61,44 55,52 L50,60 L30,60 Z"
              stroke="#15803d"
              strokeWidth="2.5"
              fill="#ffffff"
            />
            <path d="M34,42 Q40,32 46,42" stroke="#15803d" strokeWidth="2" fill="none" />
            <line x1="32" y1="64" x2="48" y2="64" stroke="#15803d" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="34" y1="69" x2="46" y2="69" stroke="#15803d" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>

        {/* Open Book Doodle (Mid-Left) */}
        <div className="doodle-item doodle-openbook">
          <svg viewBox="0 0 90 70" fill="none" className="doodle-svg">
            <path
              d="M45,20 C35,14 18,16 8,24 L8,54 C18,46 35,44 45,50 C55,44 72,46 82,54 L82,24 C72,16 55,14 45,20 Z"
              stroke="#f43f5e"
              strokeWidth="2.5"
              fill="#fff1f2"
            />
            <line x1="45" y1="20" x2="45" y2="50" stroke="#f43f5e" strokeWidth="2.5" />
            <line x1="16" y1="30" x2="36" y2="28" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="16" y1="36" x2="38" y2="34" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="16" y1="42" x2="34" y2="40" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="52" y1="28" x2="72" y2="30" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="52" y1="34" x2="74" y2="36" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="54" y1="40" x2="72" y2="42" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>

        {/* Spiral Notebook Doodle (Bottom-Right) */}
        <div className="doodle-item doodle-notebook">
          <svg viewBox="0 0 80 80" fill="none" className="doodle-svg">
            <rect x="18" y="10" width="52" height="62" rx="4" fill="#ffffff" stroke="#e11d48" strokeWidth="2" />
            <line x1="26" y1="24" x2="62" y2="24" stroke="#fecdd3" strokeWidth="1.5" />
            <line x1="26" y1="34" x2="62" y2="34" stroke="#fecdd3" strokeWidth="1.5" />
            <line x1="26" y1="44" x2="62" y2="44" stroke="#fecdd3" strokeWidth="1.5" />
            <line x1="26" y1="54" x2="62" y2="54" stroke="#fecdd3" strokeWidth="1.5" />
            <circle cx="18" cy="18" r="4" stroke="#e11d48" strokeWidth="2" fill="none" />
            <circle cx="18" cy="28" r="4" stroke="#e11d48" strokeWidth="2" fill="none" />
            <circle cx="18" cy="38" r="4" stroke="#e11d48" strokeWidth="2" fill="none" />
            <circle cx="18" cy="48" r="4" stroke="#e11d48" strokeWidth="2" fill="none" />
            <circle cx="18" cy="58" r="4" stroke="#e11d48" strokeWidth="2" fill="none" />
          </svg>
        </div>

        {/* Playful Sparkles */}
        <div className="doodle-sparkle sparkle-top">✦</div>
        <div className="doodle-sparkle sparkle-right">✦</div>
        <div className="doodle-sparkle sparkle-bottom">✦</div>
      </div>

      <main className="custom-maint-container">
        {/* TOP BRAND HEADER */}
        <header className="custom-maint-header">
          <div className="custom-maint-logo-group">
            <div className="custom-maint-brand-row">
              <img src="/imgs/logo.svg" alt="GradeMe" className="custom-maint-logo-img" />
            </div>
            <p className="custom-maint-tagline">AI Assistant for Teachers</p>
          </div>
        </header>

        {/* HERO TITLE & SUBTITLE */}
        <section className="custom-maint-hero">
          <motion.h1
            className="custom-maint-title"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            We'll Be <span className="word-orange">Right</span> <span className="word-pink">Back!</span>
          </motion.h1>

          <motion.p
            className="custom-maint-subtitle"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06, duration: 0.3 }}
          >
            We're currently doing some maintenance to make GradeMe AI faster, smoother and even more helpful for you.
          </motion.p>

          {/* Clean Estimated Time Badge in Hours */}
          <motion.div
            className="custom-maint-estimate-badge"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <span className="estimate-pulse-dot" aria-hidden="true"></span>
            <i className="fa-regular fa-clock"></i>
            <span>
              Estimated Time: <strong>{estimatedTimeInHours}</strong>
            </span>
          </motion.div>

          {/* Optional Admin Custom Reason Banner */}
          {customReason && (
            <motion.div
              className="custom-maint-notice-banner"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14, duration: 0.3 }}
            >
              <i className="fa-solid fa-bullhorn"></i>
              <span>{customReason}</span>
            </motion.div>
          )}
        </section>

        {/* MAIN STAGE: UNIFIED 3D CLASSROOM SCENE */}
        <section className="custom-maint-stage">
          <motion.div
            className="custom-maint-scene-container"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.12, duration: 0.38 }}
          >
            <MaintenanceIllustration />
          </motion.div>
        </section>

        {/* REFRESH & ADMIN ACTIONS */}
        <section className="custom-maint-actions">
          <button
            type="button"
            className="custom-maint-refresh-btn"
            onClick={handleManualCheck}
            disabled={isChecking}
          >
            <i
              className={`fa-solid fa-arrows-rotate ${isChecking ? "fa-spin" : ""}`}
            ></i>
            <span>
              {isChecking ? "Checking Status..." : "Check If We're Back"}
            </span>
          </button>

          {isSuperAdmin && (
            <button
              type="button"
              className="custom-maint-admin-btn"
              onClick={() => navigate("/admin/super")}
            >
              <i className="fa-solid fa-shield-halved"></i>
              <span>Super Admin Dashboard</span>
            </button>
          )}
        </section>

        {/* FEEDBACK TOAST */}
        <AnimatePresence>
          {checkFeedback && (
            <motion.div
              className="dock-feedback-toast"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
            >
              <i className="fa-solid fa-circle-info"></i>
              <span>{checkFeedback.message}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* BOTTOM COMMUNITY HEART PILL & SUPPORT FOOTER */}
        <footer className="custom-maint-footer">
          <p className="custom-maint-support-text">
            Need urgent assistance? Contact us at{" "}
            <a href="mailto:support@grademe-ai.com" className="custom-maint-support-link">
              support@grademe-ai.com
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}
